/**
 * The Staff App's view of the design system.
 *
 * Everything that exists in `@sonauto/ui` is re-exported from here rather than
 * reimplemented — Button, Badge, MoneyText, the three state screens, the modal,
 * the toast — so a change to a status colour or a focus ring lands in all three
 * PWAs at once. Screens import from this module rather than from the package
 * directly for exactly one reason: the handful of components below that the
 * shared system does not have, and should not, because they only make sense on
 * a phone in an apron.
 *
 * LOCAL, AND WHY
 * --------------
 *  - `Sheet`      a BOTTOM sheet, not a centred modal. On a phone held in one
 *                 hand the top of the screen is unreachable; a centred dialog
 *                 with its confirm button at the top is a two-handed control.
 *                 `@sonauto/ui` ships `Modal`/`Drawer` for the Admin app's
 *                 desktop layouts; this is the thumb-reachable equivalent.
 *  - `ConfirmSheet` the same, for confirmations.
 *  - `Card`       the shared `Card` is a titled container; this app needs a
 *                 tappable one (a whole card is a 200px touch target, which is
 *                 the right affordance on a moving floor).
 *  - `MissingEndpoint` the honest placeholder for a feature whose API does not
 *                 exist yet. Naming the endpoint on screen is deliberate: a
 *                 plausible-looking fake would be indistinguishable from a
 *                 working feature during a demo.
 *  - `PointsText` KPI points are milli-points and signed; money formatting is
 *                 wrong for them.
 *  - `Banner`, `KeyValue`, `SectionHeader` — layout atoms specific to these
 *                 screens.
 *
 * TOUCH RULES: `@sonauto/ui`'s `touch`/`touch-lg` spacing tokens are the same
 * ones this app's Tailwind preset provides, so `size="lg"` is a 64px target
 * everywhere and no screen can accidentally ship a 32px button.
 */
import { useEffect, useId, type ReactNode } from 'react';
import {
  Badge,
  Button as UiButton,
  EmptyState as UiEmptyState,
  ErrorState as UiErrorState,
  LoadingState as UiLoadingState,
  MoneyText as UiMoneyText,
  DurationText as UiDurationText,
  type ButtonSize,
  type ButtonVariant,
  type Tone,
} from '@sonauto/ui';
import { formatClock, formatMinutes, formatSignedPointsMilli } from '../lib/format';

// --- straight re-exports ----------------------------------------------------
export {
  STATUS_TONE,
  Checkbox,
  FormField,
  Input,
  PermissionGate as UiPermissionGate,
  Select,
  Textarea,
  cn,
} from '@sonauto/ui';
export type { Tone } from '@sonauto/ui';
export const EmptyState = UiEmptyState;
export const ErrorState = UiErrorState;
export const LoadingState = UiLoadingState;
export const MoneyText = UiMoneyText;
export type { ButtonSize, ButtonVariant };

// ---------------------------------------------------------------------------
// Button — the shared one, with `ariaLabel` sugar this app leans on to explain
// a disabled primary action to a screen reader.
// ---------------------------------------------------------------------------

export interface ButtonProps {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: 'button' | 'submit';
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly fullWidth?: boolean;
  readonly className?: string;
  readonly ariaLabel?: string;
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  // `lg` is 64px in the shared preset; screens use it for anything in the
  // bottom-third action dock.
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  className,
  ariaLabel,
}: ButtonProps): JSX.Element {
  return (
    <UiButton
      type={type}
      variant={variant}
      size={size}
      disabled={disabled}
      loading={loading}
      fullWidth={fullWidth}
      className={className}
      aria-label={ariaLabel}
      onClick={onClick === undefined ? undefined : () => onClick()}
    >
      {children}
    </UiButton>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge — the shared `Badge` (tone + children). `@sonauto/ui`'s own
// `StatusBadge` maps a raw API status string through a shared catalogue; this
// app labels its statuses from `lib/labels.ts`, which is exhaustive per union
// and therefore fails the build when the backend adds a state.
// ---------------------------------------------------------------------------

export function StatusBadge({
  tone = 'neutral',
  children,
}: {
  readonly tone?: Tone;
  readonly children: ReactNode;
}): JSX.Element {
  return <Badge tone={tone}>{children}</Badge>;
}

// ---------------------------------------------------------------------------
// Layout atoms
// ---------------------------------------------------------------------------

export function Card({
  children,
  className = '',
  onClick,
}: {
  readonly children: ReactNode;
  readonly className?: string;
  readonly onClick?: () => void;
}): JSX.Element {
  if (onClick !== undefined) {
    return (
      <button type="button" onClick={onClick} className={`card w-full text-left active:bg-slate-50 ${className}`}>
        {children}
      </button>
    );
  }
  return <div className={`card ${className}`}>{children}</div>;
}

export function SectionHeader({
  title,
  action,
}: {
  readonly title: string;
  readonly action?: ReactNode;
}): JSX.Element {
  return (
    <div className="mb-2 mt-6 flex items-center justify-between gap-2 first:mt-0">
      <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">{title}</h2>
      {action}
    </div>
  );
}

export function KeyValue({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: ReactNode;
  readonly hint?: string;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <div className="text-sm text-slate-600">{label}</div>
        {hint === undefined ? null : <div className="text-xs text-slate-400">{hint}</div>}
      </div>
      <div className="shrink-0 text-right font-semibold text-slate-900">{value}</div>
    </div>
  );
}

export function Banner({
  tone,
  title,
  children,
  action,
}: {
  readonly tone: Tone;
  readonly title: string;
  readonly children?: ReactNode;
  readonly action?: ReactNode;
}): JSX.Element {
  const border: Readonly<Record<Tone, string>> = {
    neutral: 'border-slate-300 bg-slate-50 text-slate-800',
    info: 'border-sky-300 bg-sky-50 text-sky-900',
    success: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    warning: 'border-amber-300 bg-amber-50 text-amber-900',
    danger: 'border-rose-300 bg-rose-50 text-rose-900',
    busy: 'border-violet-300 bg-violet-50 text-violet-900',
    accent: 'border-brand-300 bg-brand-50 text-brand-900',
  };
  return (
    <div className={`rounded-2xl border p-3 ${border[tone]}`} role={tone === 'danger' ? 'alert' : 'status'}>
      <p className="font-bold">{title}</p>
      {children === undefined ? null : <div className="mt-1 text-sm">{children}</div>}
      {action === undefined ? null : <div className="mt-3">{action}</div>}
    </div>
  );
}

/**
 * The honest placeholder for a screen the spec asks for but the API cannot yet
 * serve. It names the missing endpoint on purpose.
 */
export function MissingEndpoint({
  feature,
  endpoint,
  note,
}: {
  readonly feature: string;
  readonly endpoint: string;
  readonly note?: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
      <p className="text-base font-bold text-amber-900">Chức năng đang phát triển</p>
      <p className="mt-1 text-sm text-amber-900">{feature}</p>
      <p className="mt-2 text-sm text-amber-800">
        Cần endpoint <code className="rounded bg-amber-100 px-1 font-mono text-xs">{endpoint}</code>
      </p>
      {note === undefined ? null : <p className="mt-2 text-xs text-amber-700">{note}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Value renderers
// ---------------------------------------------------------------------------

/**
 * Minutes-or-seconds duration. `@sonauto/ui`'s `DurationText` takes seconds
 * only; most staff figures (worked time, break time) arrive from the API in
 * whole minutes, so this wrapper keeps the call sites honest about their unit.
 */
export function DurationText({
  minutes,
  seconds,
  className = '',
}: {
  readonly minutes?: number;
  readonly seconds?: number;
  readonly className?: string;
}): JSX.Element {
  if (seconds !== undefined) return <UiDurationText seconds={seconds} format="clock" className={className} />;
  return <span className={`tabular-nums ${className}`}>{formatMinutes(minutes ?? 0)}</span>;
}

/** A ticking clock display, anchored to server time by the caller. */
export function ClockText({ seconds, className = '' }: { readonly seconds: number; readonly className?: string }): JSX.Element {
  return <span className={`tabular-nums ${className}`}>{formatClock(seconds)}</span>;
}

export function PointsText({ pointsMilli }: { readonly pointsMilli: number }): JSX.Element {
  const tone = pointsMilli > 0 ? 'text-emerald-700' : pointsMilli < 0 ? 'text-rose-700' : 'text-slate-600';
  return <span className={`tabular-nums font-bold ${tone}`}>{formatSignedPointsMilli(pointsMilli)}</span>;
}

// ---------------------------------------------------------------------------
// Bottom sheet
// ---------------------------------------------------------------------------

export function Sheet({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}): JSX.Element | null {
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the sheet scrolling under the user's finger.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <button type="button" aria-label="Đóng" onClick={onClose} className="absolute inset-0 bg-slate-900/50" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative max-h-[88vh] animate-slide-up overflow-y-auto rounded-t-3xl bg-white pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] shadow-2xl"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <h2 id={titleId} className="text-lg font-bold text-slate-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            className="tap rounded-full text-2xl leading-none text-slate-500 active:bg-slate-100"
          >
            ×
          </button>
        </div>
        <div className="px-4 py-4">{children}</div>
        {footer === undefined ? null : (
          <div className="sticky bottom-0 border-t border-slate-200 bg-white px-4 py-3">{footer}</div>
        )}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Xác nhận',
  cancelLabel = 'Huỷ',
  variant = 'primary',
  loading = false,
  onConfirm,
  onCancel,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly variant?: ButtonVariant;
  readonly loading?: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): JSX.Element | null {
  return (
    <Sheet
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <div className="flex gap-3">
          <Button variant="secondary" size="lg" fullWidth onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={variant} size="lg" fullWidth onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <p className="text-base text-slate-700">{message}</p>
    </Sheet>
  );
}
